// ═══════════════════════════════════════════════════════════════
// KOTCH - DECK UTILITIES
// Deck creation, shuffling, and card management
// ═══════════════════════════════════════════════════════════════

import type { CardInstance, CardDefinition, Faction } from '../../types/kotch';
import { CARD_DEFINITIONS } from '../../data/kotch/cards';
import { getFactionDeckConfig } from '../../data/kotch/factionDecks';

// ─────────────────────────────────────────────────────────────────
// DECK CREATION
// ─────────────────────────────────────────────────────────────────

let instanceCounter = 0;

function generateInstanceId(): string {
  instanceCounter++;
  return `card_${instanceCounter}_${Date.now()}`;
}

export function resetInstanceCounter(): void {
  instanceCounter = 0;
}

export function createFullDeck(): CardInstance[] {
  return Object.values(CARD_DEFINITIONS).map((def: CardDefinition) => ({
    instanceId: generateInstanceId(),
    definitionId: def.id,
  }));
}

function createCardInstance(definitionId: string): CardInstance {
  return {
    instanceId: generateInstanceId(),
    definitionId,
  };
}

/**
 * Creates a faction-specific deck with balanced special card counts.
 * Each faction deck = faction knights + spies (to reach 10 special) + 37 basic cards = 47 total
 */
export function createFactionDeck(faction: Faction): CardInstance[] {
  const config = getFactionDeckConfig(faction);
  const cards: CardInstance[] = [];

  // Add faction-specific knights
  config.knightIds.forEach(knightId => {
    cards.push(createCardInstance(knightId));
  });

  // Add spies (up to spyCount, using spy_1 through spy_5)
  for (let i = 1; i <= config.spyCount && i <= 5; i++) {
    cards.push(createCardInstance(`spy_${i}`));
  }
  // If faction needs more than 5 spies (like Motra with 8), duplicate from the start
  for (let i = 6; i <= config.spyCount; i++) {
    cards.push(createCardInstance(`spy_${((i - 1) % 5) + 1}`));
  }

  // Add basic cards according to faction distribution
  for (let power = 1; power <= config.basicDistribution.spearman; power++) {
    cards.push(createCardInstance(`spearman_${power}`));
  }
  for (let power = 1; power <= config.basicDistribution.archer; power++) {
    cards.push(createCardInstance(`archer_${power}`));
  }
  for (let power = 1; power <= config.basicDistribution.rider; power++) {
    cards.push(createCardInstance(`rider_${power}`));
  }

  return shuffleDeck(cards);
}

// ─────────────────────────────────────────────────────────────────
// SHUFFLING
// ─────────────────────────────────────────────────────────────────

export function shuffleDeck<T>(deck: T[]): T[] {
  const shuffled = [...deck];
  // Fisher-Yates shuffle
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}

// ─────────────────────────────────────────────────────────────────
// DRAWING CARDS
// ─────────────────────────────────────────────────────────────────

export interface DrawResult {
  drawn: CardInstance[];
  remaining: CardInstance[];
}

export function drawCards(deck: CardInstance[], count: number): DrawResult {
  const actualCount = Math.min(count, deck.length);
  return {
    drawn: deck.slice(0, actualCount),
    remaining: deck.slice(actualCount),
  };
}

export function drawHand(deck: CardInstance[], handSize: number = 10): DrawResult {
  return drawCards(deck, handSize);
}

// ─────────────────────────────────────────────────────────────────
// CARD LOOKUP
// ─────────────────────────────────────────────────────────────────

export function findCardInArray(
  cards: CardInstance[],
  instanceId: string
): CardInstance | undefined {
  return cards.find(c => c.instanceId === instanceId);
}

export function removeCardFromArray(
  cards: CardInstance[],
  instanceId: string
): CardInstance[] {
  return cards.filter(c => c.instanceId !== instanceId);
}

export function getCardDefinitionFromInstance(
  instance: CardInstance
): CardDefinition | undefined {
  return CARD_DEFINITIONS[instance.definitionId];
}

// ─────────────────────────────────────────────────────────────────
// DECK ANALYSIS
// ─────────────────────────────────────────────────────────────────

export function getDeckComposition(deck: CardInstance[]): Record<string, number> {
  const composition: Record<string, number> = {
    spearman: 0,
    archer: 0,
    rider: 0,
    knight: 0,
    spy: 0,
  };

  deck.forEach(instance => {
    const def = CARD_DEFINITIONS[instance.definitionId];
    if (def) {
      composition[def.type]++;
    }
  });

  return composition;
}

export function getAveragePower(cards: CardInstance[]): number {
  if (cards.length === 0) return 0;

  const totalPower = cards.reduce((sum, instance) => {
    const def = CARD_DEFINITIONS[instance.definitionId];
    return sum + (def?.power || 0);
  }, 0);

  return totalPower / cards.length;
}

export function getTotalPower(cards: CardInstance[]): number {
  return cards.reduce((sum, instance) => {
    const def = CARD_DEFINITIONS[instance.definitionId];
    return sum + (def?.power || 0);
  }, 0);
}

export function countSpecialCards(cards: CardInstance[]): number {
  return cards.filter(instance => {
    const def = CARD_DEFINITIONS[instance.definitionId];
    return def?.category === 'special';
  }).length;
}

export function countBasicCards(cards: CardInstance[]): number {
  return cards.filter(instance => {
    const def = CARD_DEFINITIONS[instance.definitionId];
    return def?.category === 'basic';
  }).length;
}

// ─────────────────────────────────────────────────────────────────
// SORTING
// ─────────────────────────────────────────────────────────────────

export function sortCardsByType(cards: CardInstance[]): CardInstance[] {
  const typeOrder = ['spearman', 'archer', 'rider', 'knight', 'spy'];

  return [...cards].sort((a, b) => {
    const defA = CARD_DEFINITIONS[a.definitionId];
    const defB = CARD_DEFINITIONS[b.definitionId];

    if (!defA || !defB) return 0;

    const typeOrderA = typeOrder.indexOf(defA.type);
    const typeOrderB = typeOrder.indexOf(defB.type);

    if (typeOrderA !== typeOrderB) {
      return typeOrderA - typeOrderB;
    }

    return defA.power - defB.power;
  });
}

export function sortCardsByPower(cards: CardInstance[]): CardInstance[] {
  return [...cards].sort((a, b) => {
    const defA = CARD_DEFINITIONS[a.definitionId];
    const defB = CARD_DEFINITIONS[b.definitionId];

    if (!defA || !defB) return 0;
    return defB.power - defA.power; // Descending
  });
}
